export type ClientContext = {
  sessionId: string;
  environment: string;
  platform: string;
  browser: string;
  walletName?: string;
  walletTransport?: string;
  sourceChain?: string;
  destinationChain?: string;
  acquisitionSource?: string;
  referrer?: string;
  clientVersion: string;
};

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initDataUnsafe?: { start_param?: string };
      };
    };
  }
}

const CLIENT_VERSION = import.meta.env.VITE_APP_VERSION || "0.2.0";

function newSessionId() {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function getOrCreateSessionId() {
  const key = "scc:sessionId";
  try {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const id = newSessionId();
    localStorage.setItem(key, id);
    return id;
  } catch {
    return newSessionId();
  }
}

function detectPlatform() {
  const ua = navigator.userAgent;
  if (/Android/i.test(ua)) return "android";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Macintosh/i.test(ua)) return "macos";
  if (/Windows/i.test(ua)) return "windows";
  if (/Linux/i.test(ua)) return "linux";
  return "unknown";
}

function detectBrowser() {
  const ua = navigator.userAgent;
  if (/FBAN|FBAV|Instagram|Line\//i.test(ua)) return "in_app_webview";
  if (/Edg\//i.test(ua)) return "edge";
  if (/Firefox\//i.test(ua)) return "firefox";
  if (/CriOS|Chrome\//i.test(ua)) return "chrome";
  if (/Safari\//i.test(ua)) return "safari";
  return "webview_or_other";
}

function detectEnvironment() {
  if (window.Telegram?.WebApp) return "telegram";
  const standalone = window.matchMedia?.("(display-mode: standalone)").matches;
  if (standalone) return "pwa";
  const mobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  if (mobile && (window.okxwallet || window.ethereum)) return "wallet_inapp_or_mobile_browser";
  return "browser";
}

function acquisition() {
  const url = new URL(window.location.href);
  const telegramRef = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
  const referrer = telegramRef || url.searchParams.get("ref") || url.searchParams.get("startapp") || undefined;
  const source = url.searchParams.get("utm_source") || (window.Telegram?.WebApp ? "telegram" : undefined);
  return { acquisitionSource: source, referrer };
}

export const baseClientContext: ClientContext = {
  sessionId: getOrCreateSessionId(),
  environment: detectEnvironment(),
  platform: detectPlatform(),
  browser: detectBrowser(),
  clientVersion: CLIENT_VERSION,
  ...acquisition()
};

export async function initErrorReporting() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;
  try {
    const Sentry = await import("@sentry/react");
    Sentry.init({
      dsn,
      environment: import.meta.env.VITE_APP_ENV || "staging",
      release: CLIENT_VERSION,
      sendDefaultPii: false,
      tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || "0")
    });
  } catch {
    // Error reporting must never block the DApp.
  }
}

export async function captureClientError(error: unknown, context?: Record<string, unknown>) {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;
  try {
    const Sentry = await import("@sentry/react");
    Sentry.withScope((scope) => {
      if (context) scope.setContext("scc", context);
      Sentry.captureException(error);
    });
  } catch {
    // no-op
  }
}

export async function track(eventName: string, context: Partial<ClientContext> = {}, metadata?: Record<string, unknown>, operationId?: string | null) {
  const payload = {
    ...baseClientContext,
    ...context,
    operationId: operationId ?? null,
    eventName,
    metadata: metadata ? { ...metadata, browser: baseClientContext.browser } : { browser: baseClientContext.browser }
  };
  try {
    await fetch("/api/events", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true
    });
  } catch {
    // Analytics must never affect a transaction.
  }
}
